import { DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Col, Dropdown, Flex, Modal, Row, Space } from "antd";
import { useForm } from "antd/es/form/Form";
import FormItem from "antd/es/form/FormItem";
import TableAntd from "components/elements/Table";
import { FormInput, FormSelect } from "components/form/FormProvide";
import FormAntd from "components/form/FormProvide/FormAntd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const WelfareAddEmployeeTypeModal = ({
    isEmployeeTypeModalOpen = false,
    setIsEmployeeTypeModalOpen = () => { },
    employeeTypeOptions = [],
    selectedEmployeeType = [],
    onSave = () => { }
}) => {
    const [form] = useForm();
    const { t, i18n } = useTranslation();
    const [tempEmployeeTypes, setTempEmployeeTypes] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [searchTxt, setSearchTxt] = useState("");

    useEffect(() => {
        if (isEmployeeTypeModalOpen) {
            setTempEmployeeTypes(selectedEmployeeType);
            setPagination(prev => ({ ...prev, total: selectedEmployeeType.length }));
        }
    }, [isEmployeeTypeModalOpen, selectedEmployeeType]);

    const onAddEmployeeType = (employeeTypeIds) => {
        const newEmployeeTypes = employeeTypeOptions?.filter(type =>
            employeeTypeIds.includes(type.value) &&
            !tempEmployeeTypes.some(te => te.id === type.value)
        ).map(type => ({
            id: type.value,
            name: type.name || type.commonTypeName || type.label,
            nameEn: type.nameEn || type.commonTypeNameEn || type.label,
        }));

        const updated = [...tempEmployeeTypes, ...newEmployeeTypes];
        setTempEmployeeTypes(updated);
        setPagination(prev => ({ ...prev, total: updated.length }));
        form.setFieldValue("employeeTypeList", []);
    };

    const onRemoveEmployeeType = (record) => {
        const updated = tempEmployeeTypes.filter(type => type.id !== record.id);
        setTempEmployeeTypes(updated);
        setPagination(prev => ({ ...prev, total: updated.length }));
    };

    const onFilterEmployeeTypeBySearch = tempEmployeeTypes.filter(type => {
        if (!searchTxt) return true;
        const searchLower = searchTxt.toLowerCase();
        return (
            type.name?.toLowerCase().includes(searchLower) ||
            type.nameEn?.toLowerCase().includes(searchLower)
        );
    });

    const onPageEmployeeTypeChange = (page, pageSize) => {
        setPagination(prev => ({ ...prev, current: page, pageSize }));
    };

    const handleSave = () => {
        onSave(tempEmployeeTypes);
        form.resetFields();
        setSearchTxt("");
    };

    const handleCancel = () => {
        setIsEmployeeTypeModalOpen(false);
        form.resetFields();
        setSearchTxt("");
    };

    const columns = [
        {
            title: <div className="text-table" style={{ textAlign: "center" }}>{t("common.index")}</div>,
            dataIndex: "index",
            key: "index",
            render: (_, record, idx) => {
                return (
                    <span
                        style={{
                            color: "#42526D",
                            backgroundColor: "#F5F6F7",
                            borderRadius: "50%",
                            display: "inline-flex",
                            justifyContent: "center",
                            alignItems: "center",
                            margin: "auto",
                            padding: "8px",
                            minWidth: "30px",
                            height: "30px",
                        }}
                    >
                        {((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 10) + idx + 1}
                    </span>
                );
            },
            width: 90,
            align: "center",
        },
        {
            title: <div className="text-table">{t("employee.label.type")}</div>,
            dataIndex: "employeeTypeName",
            key: "employeeTypeName",
            render: (_, record) => {
                if (i18n.language === "en") {
                    return record?.nameEn || record?.commonTypeNameEn || "-";
                }
                return record?.name || record?.commonTypeName || "-";
            },
        },
        {
            title: "",
            key: "action",
            align: "center",
            width: 90,
            render: (_, record) => (
                <Space size="middle">
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: "1",
                                    onClick: () => onRemoveEmployeeType(record),
                                    label: `${t("action.delete")}`,
                                    icon: <DeleteOutlined style={{ fontSize: "18px", color: "gray" }} />,
                                },
                            ],
                        }}
                    >
                        <MoreOutlined style={{ fontSize: "25px", color: "gray" }} />
                    </Dropdown>
                </Space>
            ),
        },
    ];

    return (
        <Modal
            open={isEmployeeTypeModalOpen}
            closable={false}
            maskClosable={false}
            footer={null}
            width={i18n.language == "en" ? 780 : 720}
        >
            <Space size={24} direction="vertical" style={{ width: "100%" }}>
                <Flex vertical>
                    <h3>{t("welfare.label.add_employee_type")}</h3>
                    <span style={{ color: "#667085" }}>{t("welfare.label.add_employee_type_desc")}</span>
                </Flex>

                <FormAntd form={form}
                    onFinish={handleSave}
                >
                    <FormItem shouldUpdate>
                        {({ getFieldValue }) => {
                            return (
                                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                                    <Row>
                                        <Col span={18}>
                                            <FormSelect
                                                name="employeeTypeList"
                                                label={t("welfare.label.add_employee_type")}
                                                placeholder={t("common.pleaseSelect")}
                                                size="large"
                                                options={employeeTypeOptions}
                                                tree={true}
                                                multiple={true}
                                            />
                                        </Col>
                                        <Col span={6} style={{ alignContent: "end", textAlignLast: "end" }}>
                                            <Button
                                                onClick={() => onAddEmployeeType(getFieldValue("employeeTypeList"))}
                                                disabled={getFieldValue("employeeTypeList") == null || getFieldValue("employeeTypeList")?.length == 0}
                                                size="large"
                                                type="primary"
                                                style={{
                                                    borderRadius: "8px",
                                                    backgroundColor:
                                                        getFieldValue("employeeTypeList") == null || getFieldValue("employeeTypeList")?.length == 0
                                                            ? ""
                                                            : "var(--button-color)",
                                                }}
                                            >
                                                {t("welfare.label.add_employee_type")}
                                            </Button>
                                        </Col>
                                    </Row>

                                    {/* <Row style={{ gap: "6px" }}>
                                        <Col span={18}>
                                            <FormInput
                                                name="searchTxt"
                                                label={t("common.search_employee_type")}
                                                placeholder={t("common.fill_in")}
                                                size="large"
                                                allowClear={true}
                                            />
                                        </Col>
                                        <Col span={5} style={{ alignContent: "end" }}>
                                            <Button
                                                type="default"
                                                style={{ borderRadius: "8px", height: "40px", color: "#565656", fontWeight: "bold" }}
                                                onClick={() => setSearchTxt(getFieldValue("searchTxt") || "")}
                                            >
                                                {t("common.search")}
                                            </Button>
                                        </Col>
                                    </Row> */}

                                    <TableAntd
                                        columns={columns}
                                        dataSource={onFilterEmployeeTypeBySearch}
                                        rowKey={(record) => record?.id}
                                        noPage={true}
                                        onChange={onPageEmployeeTypeChange}
                                        pagination={{
                                            ...pagination,
                                            showTotal: (total, range) =>
                                                t("paginate.description")
                                                    .replace("{min}", range[0])
                                                    .replace("{max}", range[1])
                                                    .replace("{total}", total),
                                        }}
                                    />

                                    <Flex gap={8} justify="end">
                                        <Button
                                            type="default"
                                            style={{ borderRadius: "8px", height: "40px", color: "#565656", fontWeight: "bold" }}
                                            onClick={handleCancel}
                                        >
                                            {t("common.cancel")}
                                        </Button>

                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            style={{
                                                borderRadius: "8px",
                                                height: "40px",
                                                backgroundColor: "var(--button-color)",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {t("common.save")}
                                        </Button>
                                    </Flex>
                                </Space>
                            );
                        }}
                    </FormItem>
                </FormAntd>
            </Space>
        </Modal>
    )
}

export default WelfareAddEmployeeTypeModal;